import { format } from "date-fns";
import { TableFieldLink } from "../../../../features/talentProfile/Employment/PayslipOverview/tableItems/styled";
import {
  dateTimeFormat,
  fromIsoDateString,
  leadingZerosDateFormat,
} from "../../../helpers/formatDateTime";
import { TextFieldLight } from "../../styled/styled";
import {
  StyledTableCell,
  StyledTableCell2,
  TableCellContentContainer,
} from "./styled";
import { Box, Link, LinkProps, Typography } from "@mui/material";

export type SimpleTableCell = {
  name: string;
  value: string;
  width?: string;
  color?: string;
  type?: string;
  linkProps?: LinkProps;
  onClick?: () => void;
  component?: JSX.Element;
};

interface SimpleTableCellProps {
  value: string;
  color?: string;
  canEdit?: boolean;
  inputProps?: any;
  width?: string;
}

export const formatCellValue = (field: {
  name: string;
  value: string;
  type?: string;
}) => {
  const { value, type } = field;
  if (!type) return value;
  try {
    switch (type) {
      case "date":
        return fromIsoDateString(value, leadingZerosDateFormat);
      case "datetime":
        return value && format(new Date(value), dateTimeFormat);
      case "number":
        return parseFloat(value).toFixed(2);
      default:
        return value;
    }
  } catch {
    return value;
  }
};

// Note: SimpleTableDataCell should be used instead because TextFields are slower to render
export const SimpleTableTextCell = ({
  value,
  color,
  canEdit = false,
  inputProps,
}: SimpleTableCellProps) => (
  <StyledTableCell>
    <TextFieldLight
      InputProps={{
        ...inputProps,
        style: { fontSize: "14px", padding: "0px", color: color || "inherit" },
      }}
      size="small"
      value={value}
      disabled={!canEdit}
      fullWidth
    />
  </StyledTableCell>
);

export const SimpleTableLinkCell = ({
  value,
  color,
  canEdit,
  inputProps,
  width,
}: SimpleTableCellProps) => (
  <StyledTableCell>
    <TableFieldLink
      width={width}
      InputProps={{
        ...inputProps,
        style: { fontSize: "14px", padding: "0px", color: color || "inherit" },
      }}
      size="small"
      value={value}
      disabled={!canEdit}
    />
  </StyledTableCell>
);

export const SimpleTableDataCell = ({ value, width }: SimpleTableCellProps) => (
  <StyledTableCell2 padding="none" size="small" columnWidth={width}>
    <TableCellContentContainer columnWidth={width}>
      <Typography
        color="#00000061"
        fontSize={14}
        sx={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </TableCellContentContainer>
  </StyledTableCell2>
);

export const SimpleTableDataLinkCell = ({
  value,
  component,
  width,
  onClick,
  LinkProps = {},
}: {
  value: string;
  component?: JSX.Element;
  width?: string;
  onClick?: () => void;
  LinkProps?: LinkProps;
}) => (
  <StyledTableCell2 padding="none" size="small" columnWidth={width}>
    <TableCellContentContainer columnWidth={width}>
      <Box display="flex" alignItems="center">
        <Link
          {...LinkProps}
          onClick={onClick && onClick}
          sx={{
            cursor: "pointer",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            display: "block",
            width,
          }}
        >
          {value}
        </Link>
        {component}
      </Box>
    </TableCellContentContainer>
  </StyledTableCell2>
);

export const renderCell = (field: any, i: number) => {
  switch (field.type) {
    case "link":
      return (
        <SimpleTableLinkCell
          key={i}
          width={field.width}
          value=""
          canEdit={false}
          inputProps={{
            startAdornment: (
              <Link
                onClick={() => {
                  field.onClick && field.onClick();
                }}
                sx={{ paddingLeft: "12px", cursor: "pointer" }}
              >
                {field.value}
              </Link>
            ),
          }}
        />
      );
    case "icon":
      return (
        <SimpleTableTextCell
          key={i}
          value={""}
          canEdit={false}
          inputProps={{
            startAdornment: field.component,
          }}
        />
      );

    default:
      return (
        <SimpleTableTextCell
          key={i}
          value={formatCellValue(field)}
          canEdit={false}
          color={field.color}
        />
      );
  }
};
